/**
 * Modal for editing system audio content: source_text and ChatGPT translations.
 * Used in Lab and Material Management.
 */
import React, { useState, useEffect } from 'react';
import { FiX, FiRefreshCw, FiCheck } from 'react-icons/fi';
import { audioContentAPI } from '../services/api';
import './AudioContentEditor.css';

interface AudioContentEditorProps {
  audioId: number;
  audioFileName: string;
  onClose: () => void;
  onSaved?: () => void;
}

const AudioContentEditor: React.FC<AudioContentEditorProps> = ({
  audioId,
  audioFileName,
  onClose,
  onSaved,
}) => {
  const [sourceText, setSourceText] = useState('');
  const [translations, setTranslations] = useState<string[]>([]);
  const [sourceLang, setSourceLang] = useState<'en' | 'fa'>('en');
  const [targetLang, setTargetLang] = useState<'en' | 'fa'>('fa');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await audioContentAPI.getContent(audioId);
        if (!cancelled) {
          setSourceText(res.source_text || '');
          setTranslations(res.translations || []);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError((err as Error).message || 'Failed to load');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [audioId]);

  const detectLang = (text: string): 'en' | 'fa' => {
    const persian = (text.match(/[\u0600-\u06FF]/g) || []).length;
    const latin = (text.match(/[A-Za-z]/g) || []).length;
    return persian >= 3 && persian >= latin * 0.25 ? 'fa' : 'en';
  };

  const handleGenerate = async () => {
    const text = sourceText.trim();
    if (!text) {
      setError('Enter source text first');
      return;
    }
    const detected = detectLang(text);
    const src = detected;
    const tgt = detected === 'en' ? 'fa' : 'en';
    setSourceLang(src);
    setTargetLang(tgt);
    setGenerating(true);
    setError(null);
    try {
      const res = await audioContentAPI.generateTranslations(audioId, {
        text,
        source_lang: src,
        target_lang: tgt,
      });
      setTranslations(res.translations || []);
    } catch (err: unknown) {
      setError((err as Error).message || 'Translation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveSource = async () => {
    setSaving(true);
    setError(null);
    try {
      await audioContentAPI.updateContent(audioId, { source_text: sourceText.trim() });
      onSaved?.();
    } catch (err: unknown) {
      setError((err as Error).message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTranslations = async () => {
    if (translations.length === 0) {
      setError('Generate or enter translations first');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await audioContentAPI.saveTranslations(audioId, {
        source_text: sourceText.trim(),
        translations,
      });
      onSaved?.();
    } catch (err: unknown) {
      setError((err as Error).message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="audio-content-editor-overlay" onClick={onClose}>
      <div className="audio-content-editor-modal" onClick={(e) => e.stopPropagation()}>
        <div className="audio-content-editor-header">
          <h3>Edit Audio Content — {audioFileName}</h3>
          <button type="button" className="audio-content-editor-close" onClick={onClose}>
            {React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 20 })}
          </button>
        </div>
        <div className="audio-content-editor-body">
          {loading ? (
            <div className="audio-content-editor-loading">
              {React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, { className: 'spinner', size: 24 })}
              Loading...
            </div>
          ) : (
            <>
              {error && (
                <div className="audio-content-editor-error" onClick={() => setError(null)}>
                  {error}
                </div>
              )}
              <div className="audio-content-editor-group">
                <label>Source text (متن ویس)</label>
                <textarea
                  value={sourceText}
                  onChange={(e) => setSourceText(e.target.value)}
                  placeholder="Enter the original text of this audio..."
                  rows={4}
                />
                <button
                  type="button"
                  className="audio-content-editor-btn audio-content-editor-btn-secondary"
                  onClick={handleSaveSource}
                  disabled={saving}
                >
                  {saving ? React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, { className: 'spinner', size: 14 }) : React.createElement(FiCheck as React.ComponentType<{ size?: number }>, { size: 14 })}
                  Save source text
                </button>
              </div>
              <div className="audio-content-editor-group">
                <label>Translations (ترجمه‌ها)</label>
                <div className="audio-content-editor-langs">
                  <select value={sourceLang} onChange={(e) => setSourceLang(e.target.value as 'en' | 'fa')}>
                    <option value="en">From: English</option>
                    <option value="fa">From: Persian</option>
                  </select>
                  <select value={targetLang} onChange={(e) => setTargetLang(e.target.value as 'en' | 'fa')}>
                    <option value="en">To: English</option>
                    <option value="fa">To: Persian</option>
                  </select>
                </div>
                <button
                  type="button"
                  className="audio-content-editor-btn audio-content-editor-btn-generate"
                  onClick={handleGenerate}
                  disabled={generating || !sourceText.trim()}
                >
                  {generating ? React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, { className: 'spinner', size: 14 }) : 'Get 3 translations from ChatGPT'}
                </button>
                {translations.length > 0 && (
                  <>
                    <div className="audio-content-editor-translations">
                      {translations.map((t, i) => (
                        <div key={i} className="audio-content-editor-trans-item">
                          <span className="audio-content-editor-trans-num">{i + 1}.</span>
                          <input
                            value={t}
                            onChange={(e) => {
                              const next = [...translations];
                              next[i] = e.target.value;
                              setTranslations(next);
                            }}
                          />
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="audio-content-editor-btn audio-content-editor-btn-save"
                      onClick={handleSaveTranslations}
                      disabled={saving}
                    >
                      {saving ? React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, { className: 'spinner', size: 14 }) : <>{React.createElement(FiCheck as React.ComponentType<{ size?: number }>, { size: 14 })} Save translations</>}
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AudioContentEditor;
